import { useState, useRef, useEffect } from 'react'
import { useDashReport } from '../hooks/useDashReport'

interface OdtEspecialidad {
  Especialidad: string
  Cantidad: number
}

interface BdOdt141025Data {
  titulo: string
  subtitulo: string
  total_medicos: number
  total_especialidades: number
  especialidad_principal: string
  porcentaje_principal: number
  especialidades: OdtEspecialidad[]
}

function formatNumber(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function determinarCategoria(porcentaje: number): { label: string; color: string } {
  if (porcentaje >= 50) return { label: 'Mayoritaria', color: 'green' }
  if (porcentaje >= 20) return { label: 'Significativa', color: 'blue' }
  if (porcentaje >= 5) return { label: 'Media', color: 'yellow' }
  return { label: 'Minoritaria', color: 'gray' }
}

function getCategoryBadgeClasses(color: string) {
  const map: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    gray: 'bg-gray-100 text-gray-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-800'
}

export function BdOdt141025Page() {
  const { data, isLoading, error } = useDashReport<BdOdt141025Data>('odt/bd-141025')
  const [espExpanded, setEspExpanded] = useState(false)
  const espContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (espContentRef.current) {
      if (espExpanded) {
        espContentRef.current.style.maxHeight = espContentRef.current.scrollHeight + 'px'
        espContentRef.current.style.opacity = '1'
      } else {
        espContentRef.current.style.maxHeight = '0px'
        espContentRef.current.style.opacity = '0'
      }
    }
  }, [espExpanded])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const total = data.especialidades.reduce((sum, item) => sum + item.Cantidad, 0)
  const sorted = [...data.especialidades].sort((a, b) => b.Cantidad - a.Cantidad)
  const principal = sorted[0]
  const porcentajePrincipal = ((principal.Cantidad / total) * 100).toFixed(1)

  return (
    <div className="bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">Análisis de Cobertura - ODT</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo || 'Distribución de especialidades médicas - 14 de octubre 2025'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{formatNumber(total)}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{data.especialidades.length}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-2xl font-bold text-green-600">{principal.Especialidad}</p>
              <p className="text-sm text-gray-600 mt-1">Principal ({porcentajePrincipal}%)</p>
            </div>
          </div>
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspExpanded(!espExpanded)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="text-base lg:text-lg">Distribución por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({data.especialidades.length} especialidades)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${espExpanded ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div className="mt-4" />

          <div
            ref={espContentRef}
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: 0, opacity: 0 }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Especialidad</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Porcentaje</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sorted.map((item) => {
                    const porcentaje = ((item.Cantidad / total) * 100).toFixed(1)
                    const categoria = determinarCategoria(parseFloat(porcentaje))
                    return (
                      <tr key={item.Especialidad} className="hover:bg-gray-50">
                        <td className="px-3 lg:px-6 py-4 text-sm font-medium text-gray-900">
                          <div className="whitespace-nowrap">{item.Especialidad}</div>
                        </td>
                        <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 font-semibold">{formatNumber(item.Cantidad)}</td>
                        <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center">
                          <span className="text-blue-600 font-bold">{porcentaje}%</span>
                        </td>
                        <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-1 lg:px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryBadgeClasses(categoria.color)}`}>
                            {categoria.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                  {/* Total row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-3 lg:px-6 py-4 text-sm text-gray-900">TOTAL</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{formatNumber(total)}</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-blue-600 font-bold">100%</td>
                    <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Analisis Visual */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Grafica por Especialidad (2/3 width) */}
          <div className="lg:col-span-2 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Distribución de Especialidades Médicas
            </h3>
            <p className="text-gray-600 text-sm mb-4">Visualización proporcional de médicos por especialidad.</p>

            <div className="space-y-3">
              {sorted.map((item) => {
                const porcentaje = ((item.Cantidad / total) * 100).toFixed(1)
                return (
                  <div key={item.Especialidad} className="p-3 bg-white rounded-lg shadow-sm">
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <div className="flex flex-col sm:flex-row sm:items-center">
                            <span className="font-medium text-gray-900">{item.Especialidad}</span>
                            <span className="text-xs text-gray-500 sm:ml-2">{formatNumber(item.Cantidad)} médicos</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${porcentaje}%` }} />
                        </div>
                        <span className="font-bold text-blue-600">{porcentaje}%</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Sidebar - Resumen Total */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 shadow-sm h-full flex flex-col justify-center">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-sm">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Cobertura Total
            </h3>

            <div className="text-center py-4">
              {/* Grafico de acelerador con numero en el centro */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg className="w-32 h-32" viewBox="0 0 100 100">
                  {/* Arco de fondo (gris) */}
                  <path d="M 20 50 A 30 30 0 0 1 80 50" stroke="#e5e7eb" strokeWidth="8" fill="none" strokeLinecap="round" />
                  {/* Arco de progreso (azul) - 100% */}
                  <path d="M 20 50 A 30 30 0 0 1 80 50" stroke="#3b82f6" strokeWidth="8" fill="none" strokeLinecap="round" />
                </svg>

                {/* Numero en el centro */}
                <div className="absolute inset-0 flex items-center justify-center mt-12">
                  <div className="text-4xl font-bold text-blue-600">{formatNumber(total)}</div>
                </div>
              </div>

              {/* Porcentaje */}
              <div className="text-base font-medium text-blue-400 mb-2">
                100%
              </div>

              {/* Label */}
              <div className="flex items-center justify-center text-sm text-gray-600">
                <svg className="w-4 h-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Médicos Registrados
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer sutil */}
      <footer className="mt-12 py-6 text-center">
        <div className="text-xs text-gray-400">
          <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
            Tonal Enterprise
          </a>
          <span className="mx-2">&bull;</span>
          <span>&copy; 2025 Tonal Strategies</span>
        </div>
      </footer>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
